"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { MatchupHeader, SectionHeader } from "fansunited-sports-ui";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useTeamPageData } from "@/views/team/context/TeamPageDataContext";
import { BettingOdds } from "@/features/odds/components/BettingOdds";
import type { FUSportsMatchWithNativeId } from "@/utilities/data/types";
import type { AdaptedTeamEvent, AdaptedEventCompetitor } from "@/utilities/data/team/types";
import { useLiveMatchData } from "@/features/live-data/useLiveMatchData";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
interface LiveMatchHighlightProps {
  match: AdaptedTeamEvent;
}
function getTeamBackgroundCSS(competitor: AdaptedEventCompetitor): string {
  return competitor.metadata?.branding?.primaryColor ?? "#404040";
}

/**
 * useLiveMatchData expects an FUSportsMatch-shaped object; synthesize one from
 * the compact event. The hook re-polls the livescore endpoint — initial values
 * from the event are seed data only.
 */
function toLiveMatchShim(event: AdaptedTeamEvent): FUSportsMatchWithNativeId {
  return {
    id: event.id,
    fansUnitedMatchId: event.fansUnitedMatchId,
    score: {
      competitorOne: event.score.fullTime.competitorOne ?? "0",
      competitorTwo: event.score.fullTime.competitorTwo ?? "0"
    },
    timing: {
      kickoffTime: event.date
    },
    status: event.status,
    competitorOne: event.competitorOne,
    competitorTwo: event.competitorTwo
  } as unknown as FUSportsMatchWithNativeId;
}
export const LiveMatchHighlight: React.FC<LiveMatchHighlightProps> = t0 => {
  const $ = _c(109);
  const {
    match: event
  } = t0;
  const {
    standings
  } = useTeamPageData();
  const labels = useLabels();
  const nav = useNavigation();
  const {
    hasOdds
  } = useTenantSettings();
  let t1;
  if ($[0] !== event.fansUnitedMatchId || $[1] !== event.id || $[2] !== nav) {
    t1 = nav.match(event.fansUnitedMatchId || event.id);
    $[0] = event.fansUnitedMatchId;
    $[1] = event.id;
    $[2] = nav;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const matchHref = t1;
  const competitorOne = event.competitorOne;
  const competitorTwo = event.competitorTwo;
  let t2;
  if ($[4] !== event) {
    t2 = toLiveMatchShim(event);
    $[4] = event;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const liveMatchShim = t2;
  let t3;
  if ($[6] !== liveMatchShim) {
    t3 = {
      match: liveMatchShim
    };
    $[6] = liveMatchShim;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    score,
    matchMinute
  } = useLiveMatchData(t3);
  let t4;
  if ($[8] !== standings?.entries) {
    t4 = teamId => {
      const entry = standings?.entries.find(e => e.competitor.fansUnitedTeamId === teamId || e.competitor.id === teamId);
      return entry?.rank;
    };
    $[8] = standings?.entries;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const getTeamPosition = t4;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[10] !== competitorOne || $[11] !== competitorTwo || $[12] !== getTeamPosition || $[13] !== labels.matches.liveNow || $[14] !== matchHref) {
    const competitorOnePosition = getTeamPosition(competitorOne.id);
    const competitorTwoPosition = getTeamPosition(competitorTwo.id);
    const formatPosition = _temp;
    let t18;
    if ($[30] !== competitorOne) {
      t18 = getTeamBackgroundCSS(competitorOne);
      $[30] = competitorOne;
      $[31] = t18;
    } else {
      t18 = $[31];
    }
    const leftBackground = t18;
    let t19;
    if ($[32] !== competitorTwo) {
      t19 = getTeamBackgroundCSS(competitorTwo);
      $[32] = competitorTwo;
      $[33] = t19;
    } else {
      t19 = $[33];
    }
    const rightBackground = t19;
    let t20;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = {
        typography: {
          name: "text-sm uppercase font-semibold text-foreground"
        }
      };
      $[34] = t20;
    } else {
      t20 = $[34];
    }
    let t21;
    if ($[35] !== labels.matches.liveNow) {
      t21 = {
        name: labels.matches.liveNow,
        styles: t20
      };
      $[35] = labels.matches.liveNow;
      $[36] = t21;
    } else {
      t21 = $[36];
    }
    let t22;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = {
        container: "mb-2"
      };
      $[37] = t22;
    } else {
      t22 = $[37];
    }
    if ($[38] !== t21) {
      t17 = <SectionHeader entity={t21} styles={t22} />;
      $[38] = t21;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    T1 = Link;
    t14 = matchHref;
    t15 = false;
    t16 = "block match-card-surface rounded-lg overflow-hidden";
    t11 = "relative overflow-hidden rounded-lg";
    let t23;
    if ($[40] !== leftBackground) {
      t23 = <div className="w-1/2" style={{
        backgroundColor: leftBackground
      }} />;
      $[40] = leftBackground;
      $[41] = t23;
    } else {
      t23 = $[41];
    }
    let t24;
    if ($[42] !== rightBackground) {
      t24 = <div className="w-1/2" style={{
        backgroundColor: rightBackground
      }} />;
      $[42] = rightBackground;
      $[43] = t24;
    } else {
      t24 = $[43];
    }
    if ($[44] !== t23 || $[45] !== t24) {
      t12 = <div className="absolute inset-0 flex">{t23}{t24}</div>;
      $[44] = t23;
      $[45] = t24;
      $[46] = t12;
    } else {
      t12 = $[46];
    }
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <div className="absolute inset-0 bg-linear-to-t from-black/50 to-transparent" />;
      $[47] = t13;
    } else {
      t13 = $[47];
    }
    t10 = "relative";
    T0 = MatchupHeader;
    t8 = "stacked";
    const t25 = competitorOne.assets?.logo;
    const t26 = competitorOne.country?.code;
    const t27 = competitorOne.country?.flag;
    const t28 = competitorOne.metadata?.branding?.primaryColor;
    const t29 = competitorOne.metadata?.branding?.secondaryColor;
    let t30;
    if ($[48] !== t25 || $[49] !== t26 || $[50] !== t27 || $[51] !== t28 || $[52] !== t29) {
      t30 = <TeamLogo src={t25} size="lg" countryCode={t26} countryFlag={t27} primaryColor={t28} secondaryColor={t29} />;
      $[48] = t25;
      $[49] = t26;
      $[50] = t27;
      $[51] = t28;
      $[52] = t29;
      $[53] = t30;
    } else {
      t30 = $[53];
    }
    const t31 = competitorOne.name;
    const t32 = formatPosition(competitorOnePosition);
    let t33;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = {
        imageBorderRadius: "md",
        imageSize: "lg"
      };
      $[54] = t33;
    } else {
      t33 = $[54];
    }
    if ($[55] !== competitorOne.name || $[56] !== t30 || $[57] !== t32) {
      t9 = {
        image: t30,
        name: t31,
        subtitle: t32,
        styles: t33
      };
      $[55] = competitorOne.name;
      $[56] = t30;
      $[57] = t32;
      $[58] = t9;
    } else {
      t9 = $[58];
    }
    const t34 = competitorTwo.assets?.logo;
    const t35 = competitorTwo.country?.code;
    const t36 = competitorTwo.country?.flag;
    const t37 = competitorTwo.metadata?.branding?.primaryColor;
    const t38 = competitorTwo.metadata?.branding?.secondaryColor;
    if ($[59] !== t34 || $[60] !== t35 || $[61] !== t36 || $[62] !== t37 || $[63] !== t38) {
      t5 = <TeamLogo src={t34} size="lg" countryCode={t35} countryFlag={t36} primaryColor={t37} secondaryColor={t38} />;
      $[59] = t34;
      $[60] = t35;
      $[61] = t36;
      $[62] = t37;
      $[63] = t38;
      $[64] = t5;
    } else {
      t5 = $[64];
    }
    t6 = competitorTwo.name;
    t7 = formatPosition(competitorTwoPosition);
    $[10] = competitorOne;
    $[11] = competitorTwo;
    $[12] = getTeamPosition;
    $[13] = labels.matches.liveNow;
    $[14] = matchHref;
    $[15] = T0;
    $[16] = T1;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
    $[23] = t16;
    $[24] = t17;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    T0 = $[15];
    T1 = $[16];
    t10 = $[17];
    t11 = $[18];
    t12 = $[19];
    t13 = $[20];
    t14 = $[21];
    t15 = $[22];
    t16 = $[23];
    t17 = $[24];
    t5 = $[25];
    t6 = $[26];
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t18;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      imageBorderRadius: "md",
      imageSize: "lg"
    };
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  let t19;
  if ($[66] !== t5 || $[67] !== t6 || $[68] !== t7) {
    t19 = {
      image: t5,
      name: t6,
      subtitle: t7,
      styles: t18
    };
    $[66] = t5;
    $[67] = t6;
    $[68] = t7;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  const t20 = score?.competitorOne || "0";
  const t21 = score?.competitorTwo || "0";
  let t22;
  if ($[70] !== t20 || $[71] !== t21) {
    t22 = <div className="text-3xl font-bold text-white">{t20} - {t21}</div>;
    $[70] = t20;
    $[71] = t21;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  const t23 = matchMinute ? ` • ${matchMinute}'` : "";
  let t24;
  if ($[73] !== labels.matches.live || $[74] !== t23) {
    t24 = <div className="text-sm text-white/80 animate-pulse-subtle uppercase">{labels.matches.live}{t23}</div>;
    $[73] = labels.matches.live;
    $[74] = t23;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  let t25;
  if ($[76] !== t22 || $[77] !== t24) {
    t25 = <div className="text-center">{t22}{t24}</div>;
    $[76] = t22;
    $[77] = t24;
    $[78] = t25;
  } else {
    t25 = $[78];
  }
  let t26;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      background: "",
      container: "",
      gap: "4",
      centerTypography: "text-sm text-muted-foreground font-medium"
    };
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  let t27;
  if ($[80] !== T0 || $[81] !== t19 || $[82] !== t25 || $[83] !== t8 || $[84] !== t9) {
    t27 = <T0 variant={t8} leftEntity={t9} rightEntity={t19} leftBackground="" rightBackground="" overlay="" centerContent={t25} styles={t26} />;
    $[80] = T0;
    $[81] = t19;
    $[82] = t25;
    $[83] = t8;
    $[84] = t9;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  let t28;
  if ($[86] !== event.date || $[87] !== event.fansUnitedMatchId || $[88] !== event.id || $[89] !== hasOdds) {
    t28 = hasOdds && <div className="px-4 pb-3"><BettingOdds matchId={event.fansUnitedMatchId || event.id} markets={["1x2"]} isLive={true} market="FT_1X2" kickoffAt={new Date(event.date).toISOString()} /></div>;
    $[86] = event.date;
    $[87] = event.fansUnitedMatchId;
    $[88] = event.id;
    $[89] = hasOdds;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] !== t10 || $[92] !== t27 || $[93] !== t28) {
    t29 = <div className={t10}>{t27}{t28}</div>;
    $[91] = t10;
    $[92] = t27;
    $[93] = t28;
    $[94] = t29;
  } else {
    t29 = $[94];
  }
  let t30;
  if ($[95] !== t11 || $[96] !== t12 || $[97] !== t13 || $[98] !== t29) {
    t30 = <div className={t11}>{t12}{t13}{t29}</div>;
    $[95] = t11;
    $[96] = t12;
    $[97] = t13;
    $[98] = t29;
    $[99] = t30;
  } else {
    t30 = $[99];
  }
  let t31;
  if ($[100] !== T1 || $[101] !== t14 || $[102] !== t15 || $[103] !== t16 || $[104] !== t30) {
    t31 = <T1 href={t14} prefetch={t15} className={t16}>{t30}</T1>;
    $[100] = T1;
    $[101] = t14;
    $[102] = t15;
    $[103] = t16;
    $[104] = t30;
    $[105] = t31;
  } else {
    t31 = $[105];
  }
  let t32;
  if ($[106] !== t17 || $[107] !== t31) {
    t32 = <div>{t17}{t31}</div>;
    $[106] = t17;
    $[107] = t31;
    $[108] = t32;
  } else {
    t32 = $[108];
  }
  return t32;
};
function _temp(position) {
  if (!position) {
    return "";
  }
  const suffix = ["th", "st", "nd", "rd"];
  const v = position % 100;
  return position + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
}